import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  balanceService,
  billingAccountsService,
  promiseOfPaymentService,
  transactionService,
} from '@dch/billing-business-logic';
import { JWT_EXPIRED_TOKEN_CODE } from 'src/app/my-bill/config/my-bill.constants';
import { billingBalanceFeatureKey } from '../reducers';
import { BillingBalanceState } from '../reducers/billing-balance.reducer';
import {
  selectPaymentInfoByAccountNumber,
  selectPaymentInfoHasError,
} from './billing-payment-info.selectors';
import {
  selectBillingTransactionsHasError,
  selectTransactionsByAccountNumber,
} from './billing-transactions.selectors';
import {
  selectCustomerAccountState,
  selectCustomerLanguage,
} from './customer-account.selector';
import { selectPromiseOfPayment } from './promise-of-payment.selectors';

export const selectBillingBalance = createFeatureSelector<BillingBalanceState>(
  billingBalanceFeatureKey
);

export const selectViewableBillingAccounts = createSelector(
  selectBillingBalance,
  (state: BillingBalanceState) => state.viewableBillingAccounts
);

export const selectCustomerPaymentProfile = createSelector(
  selectBillingBalance,
  (state: BillingBalanceState) =>
    state.billingBalance.result?.customerPaymentProfile
);

export const selectSelectedBillingAccount = createSelector(
  selectBillingBalance,
  (state: BillingBalanceState) => state.selectedBillingAccount
);

export const selectSelectedBillingAccountBalance = createSelector(
  selectSelectedBillingAccount,
  (selectedBillingAccount) =>
    selectedBillingAccount ? Number(selectedBillingAccount.balance) : 0.0
);

export const selectSelectedBillingAccountOverdueBalance = createSelector(
  selectSelectedBillingAccount,
  (selectedBillingAccount) =>
    selectedBillingAccount ? Number(selectedBillingAccount.overdueBalance) : 0.0
);

export const selectShowBalanceByAccountNumber = (accountNumber: string) =>
  createSelector(
    selectTransactionsByAccountNumber(accountNumber),
    selectBillingBalance,
    (billingTransactions, billingBalance) =>
      billingTransactions &&
      balanceService.shouldDisplayBalance(billingTransactions.result) &&
      !balanceService.hasNoDataBalance(billingBalance.billingBalance) &&
      !billingTransactions.isFetching
  );

export const selectShouldDisplayPayButtonInPOP = (
  billingAccountNumber: string
) =>
  createSelector(
    selectSelectedBillingAccountBalance,
    selectSelectedBillingAccountOverdueBalance,
    selectPromiseOfPayment(billingAccountNumber),
    selectTransactionsByAccountNumber(billingAccountNumber),
    selectPaymentInfoByAccountNumber(billingAccountNumber),
    selectCustomerLanguage,
    (
      balance,
      overdueBalance,
      promiseOfPayment,
      billingTransaction,
      paymentInfo,
      customerLanguage
    ) =>
      billingTransaction &&
      paymentInfo &&
      promiseOfPayment !== undefined &&
      promiseOfPaymentService.shouldDisplayPayButtonAtPopLevel(
        overdueBalance,
        balance,
        promiseOfPayment,
        billingTransaction,
        paymentInfo,
        customerLanguage
      )
  );

export const selectSoftOrHardRefusedDirectDebitStatus = (
  billingAccountNumber: string
) =>
  createSelector(
    selectTransactionsByAccountNumber(billingAccountNumber),
    selectPromiseOfPayment(billingAccountNumber),
    selectCustomerLanguage,
    (billingTransactions, promiseOfPayment, customerLanguage) => {
      const isRefusedDirectDebitMessageVisible =
        billingTransactions &&
        promiseOfPayment !== undefined &&
        !promiseOfPayment.isFetching &&
        transactionService.shouldDisplayRefusedDirectDebitMessage(
          billingTransactions,
          promiseOfPayment,
          customerLanguage
        );

      if (isRefusedDirectDebitMessageVisible) {
        const { softRefused, hardRefused } =
          transactionService.areTransactionsInRefusedDirectDebit(
            billingTransactions.result?.openTransactions?.billingDocuments
          );
        const bankAccount =
          billingTransactions.result?.openTransactions?.billingDocuments?.[0]
            ?.paymentInfo?.bankAccount || '';
        return { softRefused, hardRefused, bankAccount };
      } else {
        return { softRefused: false, hardRefused: false, bankAccount: '' };
      }
    }
  );

export const selectCollectionMessage = (billingAccountNumber: string) =>
  createSelector(
    selectSelectedBillingAccount,
    selectPromiseOfPayment(billingAccountNumber),
    selectCustomerLanguage,
    selectPaymentInfoByAccountNumber(billingAccountNumber),
    selectTransactionsByAccountNumber(billingAccountNumber),
    (
      selectedBillingAccount,
      promiseOfPayment,
      customerLanguage,
      paymentInfo,
      transactions
    ) => {
      if (
        transactions &&
        paymentInfo &&
        promiseOfPayment !== undefined &&
        !promiseOfPayment.isFetching &&
        promiseOfPaymentService.shouldDisplayPopMessage(
          promiseOfPayment,
          customerLanguage,
          selectedBillingAccount.overdueBalance,
          transactions.result?.openTransactions.billingDocuments ?? []
        )
      ) {
        return promiseOfPayment.result?.collectionMessage[customerLanguage];
      }
    }
  );

export const selectBillingAccountByAccountNumber = (accountNumber: string) =>
  createSelector(selectViewableBillingAccounts, (viewableBillingAccounts) =>
    viewableBillingAccounts.find(
      (account) => account.accountNumber === accountNumber
    )
  );

export const selectDisplayNumberByAccountNumber = (accountNumber: string) =>
  createSelector(
    selectBillingAccountByAccountNumber(accountNumber),
    (activeBillingAccount) =>
      billingAccountsService.getFormattedFixedAndMsisdnAssociatedNumbers(
        activeBillingAccount.associatedNumbers
      )
  );

export const selectBalanceAsNumberByAccountNumber = (accountNumber: string) =>
  createSelector(
    selectBillingAccountByAccountNumber(accountNumber),
    (billingAccount) => (billingAccount ? Number(billingAccount.balance) : 0.0)
  );

export const selectBalanceStatusByAccountNumber = (
  accountNumber: string,
  translate: (key: string) => string
) =>
  createSelector(
    selectBillingAccountByAccountNumber(accountNumber),
    selectTransactionsByAccountNumber(accountNumber),
    (billingAccount, billingTransactions) =>
      balanceService.getBalanceStatus(
        billingAccount,
        billingTransactions.result,
        translate
      )
  );

export const shouldDisplayPayButtonAtBalanceLevelSelector = (
  accountNumber: string
) =>
  createSelector(
    selectBalanceAsNumberByAccountNumber(accountNumber),
    selectShowBalanceByAccountNumber(accountNumber),
    selectTransactionsByAccountNumber(accountNumber),
    selectPaymentInfoByAccountNumber(accountNumber),
    (balance, showBalance, billingTransaction, paymentInfo) =>
      balanceService.shouldDisplayPayButtonAtBalanceLevel(
        showBalance,
        billingTransaction,
        paymentInfo,
        balance
      )
  );

export const selectHasErrorForTransactionOrPaymentInfoByAccountNumber = (
  accountNumber: string
) =>
  createSelector(
    selectTransactionsByAccountNumber(accountNumber),
    selectPaymentInfoByAccountNumber(accountNumber),
    (billingTransaction, billingPaymentInfo) => {
      if (
        Boolean(
          (billingTransaction && billingTransaction.hasError) ||
            (billingPaymentInfo && billingPaymentInfo.hasError)
        )
      ) {
        return {
          hasError: true,
          failedService: Boolean(
            billingTransaction && billingTransaction.hasError
          )
            ? 'billing-transactions'
            : 'billing-payments-paymentInfo',
        };
      } else {
        return {
          hasError: false,
        };
      }
    }
  );

export const isFetchingTransactionOrPaymentInfoByAccountNumber = (
  accountNumber: string
) =>
  createSelector(
    selectTransactionsByAccountNumber(accountNumber),
    selectPaymentInfoByAccountNumber(accountNumber),
    (billingTransaction, billingPaymentInfo) =>
      Boolean(
        (billingTransaction && billingTransaction.isFetching) ||
          (billingPaymentInfo && billingPaymentInfo.isFetching)
      )
  );

export const hasBalanceServiceFetchingError = createSelector(
  selectBillingBalance,
  (state: BillingBalanceState) => state.billingBalance.hasError
);

export const selectHasEmptyInstallbase = createSelector(
  selectCustomerAccountState,
  selectBillingBalance,
  (customerAccountState, billingBalance) =>
    !customerAccountState.hasError &&
    !customerAccountState.customerAccount?.isUr() &&
    billingBalance.billingBalance.result !== null &&
    !billingBalance.billingBalance.isFetching &&
    !billingBalance.billingBalance.hasError &&
    billingBalance.viewableBillingAccounts.length === 0
);

export const hasBillingBalanceExpiredTokenError = createSelector(
  selectBillingBalance,
  (state: BillingBalanceState) => state.errorCode === JWT_EXPIRED_TOKEN_CODE
);

export const selectCollectionCardMessage = (billingAccountNumber: string) =>
  createSelector(
    selectCollectionMessage(billingAccountNumber),
    selectSoftOrHardRefusedDirectDebitStatus(billingAccountNumber),

    (collectionMessage, softOrHardRefusedDirectDebitStatus) => {
      if (collectionMessage) {
        return {
          softRefused: false,
          hardRefused: false,
          bankAccount: '',
          collectionMessage,
        };
      } else {
        return { ...softOrHardRefusedDirectDebitStatus, collectionMessage: '' };
      }
    }
  );
